import React, { useState, useEffect } from 'react';
import { Plus, CalendarDays, Search } from 'lucide-react';
import { bookingsAPI, propertiesAPI, paymentsAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import toast from 'react-hot-toast';

const SET_PRICE_SOURCES = new Set(['whatsapp_link', 'direct']);

const SOURCE_LABELS = {
  whatsapp_link: 'WhatsApp Link',
  manual: 'Manual',
  airbnb: 'Airbnb',
  booking: 'Booking.com',
  direct: 'Direct',
};

function BookingModal({ onClose, onSave, properties }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ property_id:'', check_in:'', check_out:'', num_guests:1, total_price:'', notes:'', source:'manual', guest:{ full_name:'', email:'', phone:'', nationality:'Marocaine', id_type:'CIN', id_number:'' }});
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();
  const set = (k, v) => setForm(f => ({...f, [k]:v}));
  const setGuest = (k, v) => setForm(f => ({...f, guest:{...f.guest, [k]:v}}));

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await bookingsAPI.create(form);
      toast.success('Booking created!');
      onSave();
    } catch (err) { toast.error(err.response?.data?.message || 'Error creating booking'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 style={{ fontFamily:'Syne', fontWeight:700 }}>{t('bookings.modalTitle')}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:20 }}>×</button>
        </div>
        <div style={{ display:'flex', padding:'16px 24px 0', borderBottom:'1px solid var(--border)' }}>
          {[t('bookings.step1'), t('bookings.step2')].map((s, i) => (
            <button key={s} onClick={() => step > i+1 && setStep(i+1)} style={{ padding:'8px 16px', background:'none', border:'none', borderBottom: step===i+1 ? '2px solid var(--accent)' : '2px solid transparent', color: step===i+1 ? 'var(--accent-light)' : 'var(--text-muted)', cursor: step>i+1?'pointer':'default', fontSize:13, fontWeight:500 }}>{s}</button>
          ))}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {step === 1 && <>
              <div className="form-group"><label>{t('bookings.property')}</label>
                <select value={form.property_id} onChange={e => set('property_id', e.target.value)} required>
                  <option value="">{t('bookings.selectProperty')}</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name} — {p.city}</option>)}
                </select>
              </div>
              <div className="grid-2">
                <div className="form-group"><label>{t('bookings.checkIn')}</label><input type="date" value={form.check_in} onChange={e => set('check_in', e.target.value)} required /></div>
                <div className="form-group"><label>{t('bookings.checkOut')}</label><input type="date" value={form.check_out} onChange={e => set('check_out', e.target.value)} required /></div>
              </div>
              <div className="grid-2">
                <div className="form-group"><label>{t('bookings.numGuests')}</label><input type="number" min="1" value={form.num_guests} onChange={e => set('num_guests', e.target.value)} /></div>
                <div className="form-group"><label>{t('bookings.totalPrice')}</label><input type="number" min="0" placeholder="0" value={form.total_price} onChange={e => set('total_price', e.target.value)} /></div>
              </div>
              <div className="form-group"><label>{t('bookings.source')}</label>
                <select value={form.source} onChange={e => set('source', e.target.value)}>
                  <option value="manual">{t('sources.manual')}</option>
                  <option value="airbnb">{t('sources.airbnb')}</option>
                  <option value="booking">{t('sources.booking')}</option>
                  <option value="whatsapp">{t('sources.whatsapp')}</option>
                  <option value="direct">{t('sources.direct')}</option>
                </select>
              </div>
              <div className="form-group"><label>{t('bookings.notes')}</label><textarea rows={2} placeholder={t('bookings.notesPlaceholder')} value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize:'vertical' }} /></div>
            </>}
            {step === 2 && <>
              <div className="form-group"><label>{t('bookings.guestName')}</label><input placeholder={t('bookings.guestNamePlaceholder')} value={form.guest.full_name} onChange={e => setGuest('full_name', e.target.value)} required /></div>
              <div className="grid-2">
                <div className="form-group"><label>{t('bookings.phone')}</label><input placeholder="+212 6XX XXX XXX" value={form.guest.phone} onChange={e => setGuest('phone', e.target.value)} /></div>
                <div className="form-group"><label>{t('bookings.email')}</label><input type="email" placeholder="guest@email.com" value={form.guest.email} onChange={e => setGuest('email', e.target.value)} /></div>
              </div>
              <div className="form-group"><label>{t('bookings.nationality')}</label><input placeholder={t('bookings.nationalityDefault')} value={form.guest.nationality} onChange={e => setGuest('nationality', e.target.value)} /></div>
              <div className="grid-2">
                <div className="form-group"><label>{t('bookings.idType')}</label>
                  <select value={form.guest.id_type} onChange={e => setGuest('id_type', e.target.value)}>
                    <option value="CIN">{t('bookings.cin')}</option>
                    <option value="Passport">{t('bookings.passport')}</option>
                    <option value="Residence">{t('bookings.residence')}</option>
                  </select>
                </div>
                <div className="form-group"><label>{t('bookings.idNumber')}</label><input placeholder="AB123456" value={form.guest.id_number} onChange={e => setGuest('id_number', e.target.value)} required /></div>
              </div>
            </>}
          </div>
          <div className="modal-footer">
            {step === 1 ? <>
              <button type="button" onClick={onClose} className="btn btn-ghost">{t('action.cancel')}</button>
              <button type="button" onClick={() => { if(!form.property_id||!form.check_in||!form.check_out){toast.error(t('bookings.fillRequired'));return;} setStep(2); }} className="btn btn-primary">{t('bookings.nextStep')}</button>
            </> : <>
              <button type="button" onClick={() => setStep(1)} className="btn btn-ghost">{t('bookings.back')}</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="spinner" /> : t('bookings.create')}</button>
            </>}
          </div>
        </form>
      </div>
    </div>
  );
}

function SetPriceModal({ booking, onClose, onSave }) {
  const isEdit = !!booking.total_price;
  const [price, setPrice] = useState(booking.total_price ? String(Number(booking.total_price)) : '');
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!price || Number(price) <= 0) { toast.error('Enter a valid price'); return; }
    setLoading(true);
    try {
      await bookingsAPI.update(booking.id, {
        status: booking.status,
        check_in: booking.check_in,
        check_out: booking.check_out,
        notes: booking.notes,
        total_price: price,
      });
      await paymentsAPI.upsertByBooking(booking.id, { amount: price, status: 'paid' });
      toast.success(isEdit ? 'Price updated' : 'Price set and payment recorded');
      onSave();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save price'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:380 }}>
        <div className="modal-header">
          <h3 style={{ fontFamily:'Syne', fontWeight:700 }}>{isEdit ? t('bookings.editPrice') : t('bookings.setPrice')}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:20 }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16 }}>
              {t('bookings.guestLabel')} <strong style={{ color:'var(--text-primary)' }}>{booking.guest_name}</strong>
            </p>
            <div className="form-group">
              <label>{t('bookings.totalPriceLabel')}</label>
              <input type="number" min="1" step="0.01" placeholder="e.g. 1200" value={price} onChange={e => setPrice(e.target.value)} autoFocus required />
            </div>
            {isEdit && (
              <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:-8 }}>
                {t('bookings.currentPrice')} {Number(booking.total_price).toLocaleString()} MAD
              </p>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-ghost">{t('action.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : isEdit ? t('bookings.updatePrice') : t('bookings.confirmPayment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [setPriceBooking, setSetPriceBooking] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const { t } = useLanguage();

  const load = async () => {
    try {
      const [b, p] = await Promise.all([bookingsAPI.getAll(), propertiesAPI.getAll()]);
      setBookings(b.data.data); setProperties(p.data.data);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = bookings.filter(b => {
    const matchSearch = b.guest_name?.toLowerCase().includes(search.toLowerCase()) || b.property_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || b.status === filter;
    return matchSearch && matchFilter;
  });

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}><span className="spinner" style={{ width:32, height:32, borderWidth:3 }} /></div>;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28 }}>
        <div>
          <h1 style={{ fontSize:28, fontWeight:800, marginBottom:4 }}>{t('bookings.title')}</h1>
          <p style={{ color:'var(--text-secondary)', fontSize:14 }}>{t('bookings.count', bookings.length)}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> {t('action.newBooking')}</button>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:200 }}>
          <Search size={15} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }} />
          <input placeholder={t('bookings.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft:36 }} />
        </div>
        {['all','confirmed','cancelled','completed'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className="btn" style={{ padding:'8px 16px', background: filter===s?'var(--accent)':'var(--bg-card)', color: filter===s?'white':'var(--text-secondary)', border:'1px solid var(--border)', fontSize:13 }}>{t('status.' + s)}</button>
        ))}
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {filtered.length === 0 ? (
          <div className="empty-state" style={{ padding:'60px 20px' }}>
            <CalendarDays size={48} />
            <h3>{t('bookings.noBookings')}</h3>
            <p>{t('bookings.noBookingsDesc')}</p>
            <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> {t('action.newBooking')}</button>
          </div>
        ) : (
          <table className="table">
            <thead><tr>
              <th>{t('table.guest')}</th>
              <th>{t('table.property')}</th>
              <th>{t('table.checkIn')}</th>
              <th>{t('table.checkOut')}</th>
              <th>{t('table.guests')}</th>
              <th>{t('table.amount')}</th>
              <th>{t('table.source')}</th>
              <th>{t('table.status')}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id}>
                  <td><div style={{ fontWeight:500 }}>{b.guest_name}</div><div style={{ fontSize:12, color:'var(--text-muted)' }}>{b.id_type}: {b.id_number}</div></td>
                  <td style={{ color:'var(--text-secondary)' }}>{b.property_name}</td>
                  <td style={{ color:'var(--text-secondary)' }}>{new Date(b.check_in).toLocaleDateString('fr-MA')}</td>
                  <td style={{ color:'var(--text-secondary)' }}>{new Date(b.check_out).toLocaleDateString('fr-MA')}</td>
                  <td style={{ color:'var(--text-secondary)', textAlign:'center' }}>{b.num_guests}</td>
                  <td style={{ fontWeight:600, color:'var(--gold)' }}>{b.total_price ? `${Number(b.total_price).toLocaleString()} MAD` : '—'}</td>
                  <td><span className="badge badge-accent" style={{ fontSize:11 }}>{t('sources.' + b.source) || SOURCE_LABELS[b.source] || b.source}</span></td>
                  <td><span className={`badge badge-${b.status==='confirmed'?'success':b.status==='cancelled'?'danger':'warning'}`}>{t('status.' + b.status)}</span></td>
                  <td>
                    {SET_PRICE_SOURCES.has(b.source) && (
                      <button className="btn btn-ghost" style={{ padding:'5px 12px', fontSize:12 }} onClick={() => setSetPriceBooking(b)}>
                        {b.total_price ? t('bookings.editPrice') : t('bookings.setPrice')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {modal && <BookingModal onClose={() => setModal(false)} onSave={() => { setModal(false); load(); }} properties={properties} />}
      {setPriceBooking && <SetPriceModal booking={setPriceBooking} onClose={() => setSetPriceBooking(null)} onSave={() => { setSetPriceBooking(null); load(); }} />}
    </div>
  );
}
